'use client';

import { c as _c } from "react/compiler-runtime";
import React, { FC, JSX, ReactNode, useContext, useEffect } from 'react';
import { RootStoreContext } from '@stores/rootStore';
import { app } from '@services/firebase';
import { Auth, getAuth, onAuthStateChanged } from 'firebase/auth';
import i18n from '@services/next-i18n';
import { observer } from 'mobx-react';
import { getSnapshot } from 'mobx-state-tree';
import { useRouter } from 'next/navigation';
import { ActiveTabs, Sidebar } from './sidebar';
import { sidebarElements } from './_components/SidebarElement';
import { WaitForLoad } from '@components/spinner/Spinner';
import localforage from 'localforage';
import { GoogleAnalytics } from '@components/google-analytics';
import Cookies from 'js-cookie';
import { isPresent } from '@farmai-shared/LFUtils';
import { IUser } from '../stores/User';
import { useTranslation } from 'react-i18next';
import { useLFSearchParams } from '@services/useLFSearchParams';
import { usePathname } from 'next/navigation';
export const SplashScreen: FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-screen w-screen flex-col items-center justify-center bg-white"><div className={"\n        flex w-full flex-col items-center justify-center\n        md:flex-row\n      "}><img src="/static/assets/lf-logo-light-compact.svg" className="w-1/6" /><img src="/static/assets/lf-logo-light-text.svg" className={"\n          mt-2 block w-1/2\n          md:mt-0 md:ml-4\n        "} /></div><div className="text-3xl">Loading....</div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const LoggedInLayout: FC<{
  sidebarActiveTab?: ActiveTabs;
  className?: string;
  children: JSX.Element;
}> = observer(({
  children,
  sidebarActiveTab = sidebarElements[0].id as ActiveTabs,
  className = ''
}) => {
  const {
    searchParams
  } = useLFSearchParams();
  const {
    userStore,
    uiStore,
    organizationsStore
  } = useContext(RootStoreContext);
  const router = useRouter();
  const currentOrgId = searchParams?.get('currentOrgId');
  const initialize = () => {
    const languageCookie: string = Cookies.get('NEXT_LOCALE');
    return userStore.initialize()?.then(async () => {
      if (searchParams?.has('currentOrgId') && organizationsStore.orgIds[currentOrgId!]) {
        await localforage.setItem('currentOrganizationId', currentOrgId);
      }
    }).then(() => {
      if (!uiStore.currentOrganization) {
        localforage.getItem<string>('currentOrganizationId').then(organizationId => {
          if (organizationId) {
            return uiStore.setCurrentOrganizationId(organizationId);
          }
          uiStore.setCurrentOrganizationId(organizationsStore.organizations?.[0]?.id);
        }).catch(() => uiStore.setCurrentOrganizationId(organizationsStore.organizations?.[0]?.id));
      }
    }).then(() => userStore.fetchUser()).then(() => {
      i18n.changeLanguage(languageCookie && languageCookie !== userStore.user.settings.lang ? languageCookie : userStore.user.settings.lang);
      if (process.env.NEXT_PUBLIC_RUN_ENV !== 'test') {
        languageCookie && languageCookie !== userStore.user.settings.lang && userStore.user.save({
          ...getSnapshot<IUser>(userStore.user),
          lang: languageCookie
        });
      } else {
        userStore.user.setLanguage(languageCookie);
      }
      uiStore.setIsLoading(false);
    });
  };
  const handleFirebaseLogin = (auth: Auth) => {
    if (process.env.NEXT_PUBLIC_RUN_ENV === 'test') {
      userStore.setFirebaseToken('TOKEN');
      return initialize();
    }
    onAuthStateChanged(auth, user => {
      if (!user) {
        router.push('/login');
        return;
      }
      user.getIdToken().then(tok => {
        userStore.setFirebaseUid(user.uid);
        return userStore.setFirebaseToken(tok);
      }).then(() => {
        initialize();
      }).catch(err => ['invalid_scopes', 'access_denied'].includes(err.response?.data?.code) && router.push('/login'));
    }, error => {
      router.push('/login');
    });
  };
  useEffect(() => {
    const auth = getAuth(app);
    handleFirebaseLogin(auth);
  }, []);
  return userStore.firebaseToken ? <LoggedInLayoutWithSidebar sidebarActiveTab={sidebarActiveTab} className={className}>
        {uiStore.currentOrganization && uiStore.isReady ? children : <WaitForLoad isLoading={!uiStore.isReady}>
            {() => <h1 className="rounded bg-white p-10 text-center text-4xl text-black">
                You are currently not part of any organization. <br></br>
                Please contact Listenfield at support@listenfield.com
              </h1>}
          </WaitForLoad>}
      </LoggedInLayoutWithSidebar> : <SplashScreen />;
});
export const LoggedInLayoutWithSidebar: FC<{
  className?: string;
  children: ReactNode;
  sidebarActiveTab: ActiveTabs;
}> = observer(({
  children,
  className = '',
  sidebarActiveTab
}) => {
  const {
    uiStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const pathname = usePathname();
  const hideMobileSidebarHeader = pathname === '/cropzones/new' || pathname === '/cropzones/drafts/new';
  return <div className={`
        flex max-h-screen max-w-screen flex-row
        print:justify-center
      `}>
      {/* Moving this in app.js does not seem to work on first load... */}
      {process.env.NEXT_PUBLIC_RUN_ENV === 'production' && isPresent(process.env.NEXT_PUBLIC_GA_ID) && <GoogleAnalytics measurementId={process.env.NEXT_PUBLIC_GA_ID}></GoogleAnalytics>}

      <Sidebar sidebarActiveTab={sidebarActiveTab} collapsed={!uiStore.sidebar.open} toggleSidebar={() => uiStore.sidebar.toggleSidebar()} />

      <div className={`
          ${className}
          flex min-h-svh grow flex-col overflow-hidden
          print:overflow-visible
        `}>
        <div className={`
            my-4 flex items-center gap-x-3
            md:hidden
            print:hidden
            ${hideMobileSidebarHeader ? 'hidden' : 'block'}
          `}>
          <button className="pl-2" onClick={() => uiStore.sidebar.toggleSidebar()}>
            <img src={`/static/assets/sidebar-menu.svg`} />
          </button>
          <h1 className="text-3xl">{t(`sidebar_${sidebarActiveTab}`)}</h1>
        </div>
        {children}
      </div>
    </div>;
});